import React, { useContext, useEffect, useMemo, useState } from 'react';

const MOBILE_BREAKPOINT_QUERY = '(max-width: 767px)';

type SidebarContextType = {
	isHidden: boolean;
	showSidebar: () => void;
	hideSidebar: () => void;
};

export const SidebarContext = React.createContext<SidebarContextType>({
	isHidden: false,
	showSidebar: () => {
		return;
	},
	hideSidebar: () => {
		return;
	},
});

export const useSidebar = () => {
	const context = useContext(SidebarContext);
	if (context === null) {
		throw new Error(
			'useSidebar can only be used within a SidebarContextProvider',
		);
	}
	return context;
};

type SidebarContextProviderProps = {
	children: JSX.Element;
};

export const SidebarContextProvider = (props: SidebarContextProviderProps) => {
	const [isHidden, setIsHidden] = useState(
		() =>
			typeof window !== 'undefined' &&
			window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches,
	);

	useEffect(() => {
		const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
		const handleChange = (event: MediaQueryListEvent) =>
			setIsHidden(event.matches);

		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, []);

	const providedValue = useMemo<SidebarContextType>(
		() => ({
			isHidden,
			showSidebar: () => setIsHidden(false),
			hideSidebar: () => setIsHidden(true),
		}),
		[isHidden],
	);

	return (
		<SidebarContext.Provider value={providedValue}>
			{props.children}
		</SidebarContext.Provider>
	);
};
